import { useEffect, useRef } from "react";
import type { Stripe, StripeEmbeddedCheckout } from "@stripe/stripe-js";

interface EmbeddedCheckoutFrameProps {
  stripePromise: Promise<Stripe | null>;
  clientSecret: string;
  onComplete?: () => void;
}

// Mounts Stripe Embedded Checkout directly instead of via @stripe/react-stripe-js.
// That library's EmbeddedCheckoutProvider leaks the checkout object when unmounted
// while still initialising (stale closure in its cleanup), so reopening the modal
// throws "You cannot have multiple Embedded Checkout objects."
export function EmbeddedCheckoutFrame({ stripePromise, clientSecret, onComplete }: EmbeddedCheckoutFrameProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Checkout is only initialised once, so read the latest callback through a ref
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    let checkout: StripeEmbeddedCheckout | null = null;
    let cancelled = false;

    stripePromise
      .then(stripe => stripe?.initEmbeddedCheckout({
        clientSecret,
        onComplete: () => onCompleteRef.current?.(),
      }))
      .then(c => {
        if (!c) return;
        if (cancelled || !ref.current) {
          c.destroy();

          return;
        }

        checkout = c;
        c.mount(ref.current);
      });

    return () => {
      cancelled = true;
      checkout?.destroy();
    };
  }, []);

  return <div ref={ ref } />;
}
